import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  MessageSquare,
  CalendarCheck,
  CalendarDays,
  Building2,
  Users,
  Home,
  Settings,
  Database,
  PlayCircle,
  ListChecks,
  Menu,
  X,
  Moon,
} from "lucide-react";
import { useBrand } from "../context/BrandContext";
import { useDemo } from "../context/DemoContext";

const NAV = [
  { to: "/", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/asistente", label: "Asistente 24/7", icon: MessageSquare },
  { to: "/visitas", label: "Coordinación de visitas", icon: CalendarCheck },
  { to: "/agenda", label: "Agenda semanal", icon: CalendarDays },
  { to: "/alquileres", label: "Alquileres", icon: Home },
  { to: "/portales", label: "Portal inquilino/propietario", icon: Users },
  { to: "/propiedades", label: "Propiedades e interesados", icon: Building2 },
  { to: "/tareas", label: "Tareas", icon: ListChecks },
];

const NAV_DOCS = [
  { to: "/configuracion", label: "Configuración", icon: Settings },
  { to: "/esquema-datos", label: "Esquema de datos", icon: Database },
  { to: "/recorrido-demo", label: "Recorrido de demo", icon: PlayCircle },
];

function Monograma({ texto }) {
  return (
    <div
      className="flex h-9 w-9 items-center justify-center rounded-md text-sm font-bold bg-brand text-white shrink-0"
      data-testid="brand-monogram"
    >
      {texto}
    </div>
  );
}

function NavItem({ item, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      data-testid={`nav-${item.to === "/" ? "home" : item.to.slice(1)}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          isActive
            ? "bg-brand-light text-brand"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className="h-[18px] w-[18px]" style={isActive ? { color: "var(--brand)" } : {}} />
          <span>{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

export function AppLayout({ children }) {
  const { brand } = useBrand();
  const { start: startDemo } = useDemo();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const cerrar = () => setOpen(false);

  const Sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-200">
        <Monograma texto={brand.monograma} />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900" data-testid="brand-name">
            {brand.nombre}
          </p>
          <p className="text-xs text-slate-500">Gestión inmobiliaria</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV.map((i) => (
          <NavItem key={i.to} item={i} onClick={cerrar} />
        ))}
        <div className="pt-4 pb-1 px-3">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">
            Demo
          </p>
        </div>
        {NAV_DOCS.map((i) => (
          <NavItem key={i.to} item={i} onClick={cerrar} />
        ))}
      </nav>
      <div className="border-t border-slate-200 px-4 py-3 space-y-2">
        <button
          onClick={() => { cerrar(); startDemo(); }}
          data-testid="start-demo-button"
          className="flex w-full items-center justify-center gap-2 rounded-md btn-brand px-3 py-2 text-xs font-medium"
        >
          <PlayCircle className="h-4 w-4" /> Iniciar demo guiada
        </button>
        <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
          <Moon className="h-4 w-4 text-slate-400" />
          <span>Oficina cerrada — asistente activo 24/7</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="App min-h-screen bg-[#f8fafc]">
      {/* Sidebar escritorio */}
      <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-64 bg-white border-r border-slate-200">
        {Sidebar}
      </aside>

      {/* Topbar mobile */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <Monograma texto={brand.monograma} />
          <span className="text-sm font-semibold text-slate-900">{brand.nombre}</span>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-button"
          className="rounded-md p-2 text-slate-600 hover:bg-slate-100"
        >
          <Menu className="h-5 w-5" />
        </button>
      </header>

      {/* Off-canvas mobile */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={cerrar} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl">
            <button
              onClick={cerrar}
              className="absolute right-3 top-3 rounded-md p-2 text-slate-500 hover:bg-slate-100"
              data-testid="mobile-menu-close"
            >
              <X className="h-5 w-5" />
            </button>
            {Sidebar}
          </div>
        </div>
      )}

      <main className="md:pl-64">
        <div
          key={location.pathname}
          className="mx-auto max-w-[1400px] px-4 py-6 md:px-8 md:py-8"
        >
          {children}
        </div>
      </main>
    </div>
  );
}

export function PageHeader({ titulo, descripcion, children }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
          {titulo}
        </h1>
        {descripcion && <p className="mt-1 text-sm text-slate-500 max-w-2xl">{descripcion}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}
